import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import apiClient from "@/lib/apiClient";

import { 
  History, 
  ArrowRight, 
  AlertTriangle, 
  CheckCircle,
  Search,
  Filter,
  Trash2,
  Eye
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AnalysisHistory() {
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchAnalyses();
  }, []);

  const fetchAnalyses = async () => {
    try {
      const response = await apiClient.get(`${API}/analyses`);
      setAnalyses(response.data.reverse());
    } catch (error) {
      console.error("Error fetching analyses:", error);
      toast.error("Erreur lors du chargement de l'historique");
    } finally {
      setLoading(false);
    }
  };

  const getRiskColor = (score) => {
    if (score <= 3) return "risk-low";
    if (score <= 7) return "risk-medium";
    return "risk-high";
  };

  const getRiskLabel = (score) => {
    if (score <= 3) return "Faible";
    if (score <= 7) return "Modéré";
    return "Élevé";
  };

  const filteredAnalyses = analyses.filter(analysis => {
    if (!searchTerm) return true;
    const dateStr = new Date(analysis.created_at).toLocaleDateString("fr-FR");
    return dateStr.includes(searchTerm) || 
           analysis.summary?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  return (
    <div className="animate-fade-in" data-testid="history-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-slate-900 tracking-tight font-heading">
          Historique des analyses
        </h1>
        <p className="mt-2 text-slate-600">
          Consultez et gérez vos analyses précédentes
        </p>
      </div>

      {/* Search & Filters */}
      <div className="flex items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Rechercher par date ou résumé..."
            className="pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="search-input"
          />
        </div>
        <div className="text-sm text-slate-500">
          {filteredAnalyses.length} analyse{filteredAnalyses.length > 1 ? "s" : ""}
        </div>
      </div>

      {/* Analyses List */}
      {loading ? (
        <Card>
          <CardContent className="py-12 text-center text-slate-500">
            Chargement...
          </CardContent>
        </Card>
      ) : filteredAnalyses.length === 0 ? (
        <Card data-testid="no-history">
          <CardContent className="py-12 text-center">
            <History className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">
              {searchTerm ? "Aucun résultat" : "Aucune analyse"}
            </h3>
            <p className="text-slate-500 mb-4">
              {searchTerm 
                ? "Aucune analyse ne correspond à votre recherche" 
                : "Commencez par créer votre première analyse"}
            </p>
            {!searchTerm && (
              <Link to="/new-analysis">
                <Button data-testid="new-analysis-btn">
                  Nouvelle analyse
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredAnalyses.map((analysis) => (
            <Card 
              key={analysis.id} 
              className="card-hover"
              data-testid={`history-item-${analysis.id}`}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-6">
                    {/* Risk Score */}
                    <div className={`w-16 h-16 rounded-xl flex flex-col items-center justify-center ${getRiskColor(analysis.risk_score)}`}>
                      <span className="text-xl font-bold">{analysis.risk_score}</span>
                      <span className="text-[10px] uppercase tracking-wider">risque</span>
                    </div>

                    {/* Info */}
                    <div>
                      <p className="text-sm text-slate-500 mb-1">
                        {new Date(analysis.created_at).toLocaleDateString("fr-FR", {
                          weekday: "long",
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit"
                        })}
                      </p>
                      <p className="font-medium text-slate-900 mb-2">
                        {analysis.summary || "Analyse STOPP/START"}
                      </p>
                      <div className="flex items-center gap-3">
                        <Badge className="badge-stopp flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3" />
                          {analysis.stopp_alerts?.length || 0} STOPP
                        </Badge>
                        <Badge className="badge-start flex items-center gap-1">
                          <CheckCircle className="h-3 w-3" />
                          {analysis.start_recommendations?.length || 0} START
                        </Badge>
                        <Badge className="badge-has">
                          {analysis.has_recommendations?.length || 0} HAS
                        </Badge>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <Link to={`/analysis/${analysis.id}`}>
                      <Button variant="outline" data-testid={`view-${analysis.id}`}>
                        <Eye className="mr-2 h-4 w-4" />
                        Voir détails
                      </Button>
                    </Link>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
